import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { NotificationStore, NotificationKind } from '@/app/core/notifications/notification.store';
import { MIcon } from './m-icon';

/**
 * Toast host — renders the active toasts from NotificationStore at a fixed
 * top-center position. Mounted once at app root (replaces MatSnackBar).
 * Not: alt-orta agentic asistan butonuyla çakışmasın diye üstte konumlanır.
 */
@Component({
  selector: 'm-toaster-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MIcon],
  host: { class: 'contents' },
  template: `
    <div
      class="pointer-events-none fixed top-4 left-1/2 z-[1000] flex w-full max-w-md -translate-x-1/2 flex-col items-center gap-2 px-4"
      aria-live="polite"
      aria-atomic="false"
    >
      @for (toast of store.toasts(); track toast.id) {
        <div
          [class]="toastClasses(toast.kind)"
          class="pointer-events-auto flex w-full items-start gap-3 rounded-xl px-4 py-3 shadow-lg ring-1 backdrop-blur"
          role="status"
        >
          <m-icon
            [name]="iconFor(toast.kind)"
            size="sm"
            [class]="iconColorFor(toast.kind)"
            class="shrink-0 mt-0.5"
          />
          <p class="flex-1 text-sm font-medium leading-snug">{{ toast.message }}</p>
          @if (toast.action) {
            <button
              type="button"
              class="shrink-0 rounded-md px-2 py-0.5 text-xs font-semibold uppercase tracking-wide hover:bg-black/5 dark:hover:bg-white/10"
              (click)="store.dismiss(toast.id)"
            >
              {{ toast.action }}
            </button>
          }
        </div>
      }
    </div>
  `,
})
export class MToasterHost {
  protected store = inject(NotificationStore);

  protected toastClasses = (kind: NotificationKind) => {
    const base = 'bg-white text-neutral-900 ring-neutral-200 dark:bg-neutral-900 dark:text-neutral-100 dark:ring-neutral-700';
    switch (kind) {
      case 'success':
        return base + ' ring-green-200 dark:ring-green-900/40';
      case 'error':
        return base + ' ring-red-200 dark:ring-red-900/40';
      case 'warning':
        return base + ' ring-amber-200 dark:ring-amber-900/40';
      case 'info':
      default:
        return base;
    }
  };

  protected iconFor(kind: NotificationKind): string {
    switch (kind) {
      case 'success': return 'circle-check';
      case 'error':   return 'circle-x';
      case 'warning': return 'triangle-alert';
      case 'info':
      default:        return 'info';
    }
  }

  protected iconColorFor(kind: NotificationKind): string {
    switch (kind) {
      case 'success': return 'text-green-600 dark:text-green-400';
      case 'error':   return 'text-red-600 dark:text-red-400';
      case 'warning': return 'text-amber-600 dark:text-amber-400';
      case 'info':
      default:        return 'text-blue-600 dark:text-blue-400';
    }
  }
}
